import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Upload, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import {
  getEmployeeDocuments,
  uploadEmployeeDocument,
  deleteEmployeeDocument,
  getEmployeeDocumentDownloadUrl,
} from "@/Api/employeeDocuments";
import { DeleteBtn } from "@/components/Delete";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

export const EmployeeDocumentsManager = ({
  employeeUuid,
}: {
  employeeUuid: string;
}) => {
  const { toast } = useToastNavigate();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const fetchDocuments = async () => {
    const { data, ok } = await getEmployeeDocuments(employeeUuid);
    if (ok) setDocuments(data?.response ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeUuid]);

  const handleUpload = async () => {
    if (!name.trim() || !file) return;

    setUploading(true);
    const { data, ok } = await uploadEmployeeDocument(employeeUuid, {
      name: name.trim(),
      file,
    });
    setUploading(false);

    if (!ok) {
      toast({
        description: data?.message || "Erro ao enviar documento",
        variant: "destructive",
      });
      return;
    }

    setDocuments((prev) => [data?.response, ...prev]);
    setName("");
    setFile(null);
    toast({ description: "Documento enviado!" });
  };

  const handleDownload = async (documentUuid: string, id: number) => {
    setDownloading(id);
    const { data, ok } = await getEmployeeDocumentDownloadUrl(
      employeeUuid,
      documentUuid
    );
    setDownloading(null);
    if (!ok || !data?.response?.url) return;
    window.open(data.response.url, "_blank");
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium">Arquivos anexados</label>

      <div className="flex flex-col sm:flex-row gap-2 rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Descrição (ex: RG frente, comprovante de residência)"
          className="flex-1"
        />
        {/* input cru de propósito — o wrapper <Input> reatribui event.target.value
            no onChange (pra máscaras), o que é inválido em input[type=file] e
            zera event.target.files antes do handler rodar */}
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="flex h-10 w-full sm:max-w-[220px] bg-card rounded-md border border-input px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Button
          type="button"
          className={cn(colors.bg_primary)}
          disabled={!name.trim() || !file || uploading}
          onClick={handleUpload}
        >
          {uploading ? <LoaderSpin /> : <Upload className="size-4" />}
          Enviar
        </Button>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      )}

      {!loading && documents.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          Nenhum documento anexado ainda.
        </p>
      )}

      {documents.map((doc) => (
        <div
          key={doc.uuid}
          className="flex items-center justify-between gap-2 rounded-md border p-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="size-5 shrink-0 text-muted-foreground" />
            <div className="flex flex-col min-w-0">
              <span className="font-medium truncate">{doc.name}</span>
              <span className="text-xs text-muted-foreground truncate">
                {doc.original_filename} · enviado por{" "}
                {doc.uploaded_by?.name ?? "-"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 p-0 hover:bg-stone-950/10"
              title="Baixar"
              disabled={downloading === doc.id}
              onClick={() => handleDownload(doc.uuid, doc.id)}
            >
              <Download className="size-5 text-blue-600" />
            </Button>
            <DeleteBtn
              referenceLabel={doc.name}
              params={{
                func: (documentUuid: string) =>
                  deleteEmployeeDocument(employeeUuid, documentUuid),
                funcParams: doc.uuid,
              }}
              updateFront={() =>
                setDocuments((prev) => prev.filter((d) => d.uuid !== doc.uuid))
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
};
